const CONVERSATION_EVENT_SCHEMA = 'attunewise.conversation_event.v1'
const { normalizeUsage, usageFields } = require('./usage-billing.js')

const CHAT_ROLES = new Set(['system', 'developer', 'user', 'assistant', 'tool'])
const ROOT_CHAT_FIELDS = [
  'role',
  'content',
  'reasoning_content',
  'tool_calls',
  'tool_call_id',
  'addedToTranscript',
  'models'
]
const LEGACY_MESSAGE_FIELDS = ['addedToTranscript', 'models']
const LEGACY_SOURCE_FIELDS = [
  'modelIndex',
  'entryIndex',
  'replyContent',
  'sourceContent',
  'toolCallIndex'
]

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key)

const clean = value => {
  if (Array.isArray(value)) return value.map(clean).filter(item => item !== undefined)
  if (!value || typeof value !== 'object') return value
  const out = {}
  for (const [key, item] of Object.entries(value)) {
    if (item !== undefined) out[key] = clean(item)
  }
  return out
}

const eventKind = message => {
  const toolCalls = Array.isArray(message && message.tool_calls)
    ? message.tool_calls.filter(Boolean)
    : []
  if (toolCalls.length) return 'tool_call'
  if (message && (message.tool_call_id || message.toolCallId)) return 'tool_result'
  return `${String(message && message.role || 'message')}_message`
}

const normalizeConversationEventUsage = usage => {
  if (!usage || typeof usage !== 'object' || Array.isArray(usage)) return usage
  if (usageFields.some(field => usage[field] !== undefined)) {
    return normalizeUsage(usage)
  }
  const normalized = {}
  for (const [model, value] of Object.entries(usage)) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue
    if (!usageFields.some(field => value[field] !== undefined)) continue
    normalized[model] = normalizeUsage(value)
  }
  return Object.keys(normalized).length ? normalized : undefined
}

const assertChatMessageIR = (message, label = 'conversation event message') => {
  if (!message || typeof message !== 'object' || Array.isArray(message)) {
    throw new Error(`${label} must be an object`)
  }
  if (!CHAT_ROLES.has(message.role)) {
    throw new Error(`${label} has unsupported role: ${String(message.role)}`)
  }
  for (const key of LEGACY_MESSAGE_FIELDS) {
    if (hasOwn(message, key)) throw new Error(`${label} cannot contain ${key}`)
  }
  if (message.role === 'tool' && !message.tool_call_id) {
    throw new Error(`${label} tool role requires tool_call_id`)
  }
  return message
}

const assertConversationEventIR = (event, {
  requireIdentity = true,
  runtimeWrite = false
} = {}) => {
  if (!event || typeof event !== 'object' || Array.isArray(event)) {
    throw new Error('conversation event must be an object')
  }
  if (requireIdentity && !event.id) throw new Error('conversation event id is required')
  assertChatMessageIR(event.message)
  for (const key of ROOT_CHAT_FIELDS) {
    if (hasOwn(event, key)) {
      throw new Error(`conversation event cannot flatten message field ${key}`)
    }
  }
  if (runtimeWrite) {
    const source = event.source || {}
    for (const key of LEGACY_SOURCE_FIELDS) {
      if (hasOwn(source, key)) {
        throw new Error(`runtime conversation event source cannot contain projection field ${key}`)
      }
    }
  }
  return event
}

const normalizeConversationEventIR = (event, {
  id,
  uid,
  conversationId,
  now = Date.now()
} = {}) => {
  const normalizedId = String(id || event && (event.id || event.messageId) || '')
  const message = clean(event && event.message)
  const ts = Number(event && event.ts) || Number(now)
  const inReplyTo = event && event.inReplyTo || undefined
  const normalized = clean({
    ...(event || {}),
    id: normalizedId,
    schema: CONVERSATION_EVENT_SCHEMA,
    uid: uid || event && event.uid,
    conversationId: conversationId || event && event.conversationId,
    messageId: normalizedId,
    ts,
    inReplyTo,
    turnId: event && event.turnId || (message.role === 'user' ? normalizedId : inReplyTo || normalizedId),
    renderGroupId: event && event.renderGroupId || (
      message.tool_call_id
        ? `tool-${message.tool_call_id}`
        : Array.isArray(message.tool_calls) && message.tool_calls[0] && message.tool_calls[0].id
          ? `tool-${message.tool_calls[0].id}`
          : normalizedId
    ),
    kind: eventKind(message),
    deleted: Boolean(event && event.deleted),
    message,
    usage: normalizeConversationEventUsage(event && event.usage),
    source: {
      collection: 'ConversationEvents',
      docId: normalizedId,
      messageId: normalizedId,
      ...(event && event.source || {})
    }
  })
  return assertConversationEventIR(normalized, { runtimeWrite: true })
}

module.exports = {
  CHAT_ROLES,
  CONVERSATION_EVENT_SCHEMA,
  ROOT_CHAT_FIELDS,
  assertChatMessageIR,
  assertConversationEventIR,
  clean,
  eventKind,
  normalizeConversationEventUsage,
  normalizeConversationEventIR
}
